// Cap Table Modeler — calculation core, moved verbatim from the former
// public/calculators/cap-table.html so results stay identical.

export const STORAGE_KEY = "cpg-cap-table-v1";

export const defaultState = () => ({
  companyName: "",
  authorizedShares: 10_000_000,
  founders: [],   // {id, name, role, shares}
  poolSize: 10,   // % of fully-diluted
  poolAllocated: 0,   // % already granted
  safes: [],      // {id, investor, amount, cap, discount, converted, shares?}
  rounds: [],     // {id, name, amount, preMoney, sharesIssued?, pricePerShare?}
});

export const uid = () => Math.random().toString(36).slice(2, 9);

export const num = (v) => {
  const n = Number(String(v).replace(/,/g, ""));
  return isFinite(n) ? n : 0;
};
export const fmtInt = (n) => Math.round(n).toLocaleString("en-US");
export const fmtMoney = (n) => "$" + fmtInt(n);
export const fmtPct = (n) => (n * 100).toFixed(2) + "%";
export const fmtPrice = (n) => "$" + n.toFixed(n < 10 ? 4 : 2);

// Fully-diluted breakdown after: founders → option pool → SAFE conversion
// at the first priced round → each priced round in order.
// NOTE: mutates state.safes[].converted / .shares exactly like the original.
export function compute(state) {
  const founderShares = state.founders.reduce((s, f) => s + num(f.shares), 0);
  const holders = state.founders.map(f => ({
    id: f.id, name: f.name || "(unnamed founder)", type: "Founder",
    shares: num(f.shares),
  }));

  // Pool total = poolSize% of (founders + pool)  ⇒  pool = founders × p/(1-p)
  const pPct = Math.max(0, Math.min(40, state.poolSize)) / 100;
  const poolShares = pPct > 0 && pPct < 1 && founderShares > 0
    ? Math.round((founderShares * pPct) / (1 - pPct))
    : 0;
  holders.push({
    id: "pool", name: "Employee option pool", type: "Option pool",
    shares: poolShares,
  });

  let preRoundShares = founderShares + poolShares;

  const firstRound = state.rounds[0];
  const safeHolders = [];
  if (firstRound) {
    const roundPrice = firstRound.preMoney > 0 && preRoundShares > 0
      ? firstRound.preMoney / preRoundShares
      : 0;
    for (const s of state.safes) {
      const cap = num(s.cap);
      const disc = Math.max(0, Math.min(0.5, num(s.discount) / 100));
      const capPrice = cap > 0 && preRoundShares > 0 ? cap / preRoundShares : 0;
      const discountedPrice = roundPrice > 0 ? roundPrice * (1 - disc) : 0;
      const price = Math.min(
        capPrice > 0 ? capPrice : Infinity,
        discountedPrice > 0 ? discountedPrice : Infinity,
      );
      const shares = price > 0 && price < Infinity
        ? Math.round(num(s.amount) / price) : 0;
      s.converted = true; s.shares = shares;
      if (shares > 0) {
        safeHolders.push({
          id: "safe-" + s.id, name: s.investor || "SAFE holder",
          type: "SAFE (converted)", shares,
        });
      }
    }
  } else {
    for (const s of state.safes) { s.converted = false; s.shares = 0; }
  }
  holders.push(...safeHolders);
  const preRoundPlusSafesShares = preRoundShares
    + safeHolders.reduce((a, h) => a + h.shares, 0);

  let outstanding = preRoundPlusSafesShares;
  const roundResults = [];
  for (const r of state.rounds) {
    if (outstanding <= 0 || r.preMoney <= 0) continue;
    const pricePerShare = r.preMoney / outstanding;
    const sharesIssued = Math.round(r.amount / pricePerShare);
    outstanding += sharesIssued;
    roundResults.push({
      id: r.id, name: r.name, amount: r.amount, preMoney: r.preMoney,
      pricePerShare, sharesIssued,
      investorPct: r.amount / (r.amount + r.preMoney),
    });
    holders.push({
      id: "round-" + r.id, name: r.name + " investors",
      type: "Investor", shares: sharesIssued,
    });
  }

  const totalShares = holders.reduce((s, h) => s + h.shares, 0);
  const founderPctTotal = totalShares > 0
    ? founderShares / totalShares : 0;
  const totalRaised = state.rounds.reduce((s, r) => s + num(r.amount), 0)
    + state.safes.reduce((s, x) => s + num(x.amount), 0);
  const postMoney = state.rounds.length > 0
    ? state.rounds[state.rounds.length - 1].preMoney
      + state.rounds[state.rounds.length - 1].amount
    : 0;

  for (const h of holders) {
    h.pct = totalShares > 0 ? h.shares / totalShares : 0;
  }

  return {
    holders, totalShares, founderShares, poolShares,
    founderPctTotal, totalRaised, postMoney, roundResults,
  };
}

export function buildCsv(state) {
  const computed = compute(state);
  const rows = [
    ["CPG Cap Table — export"],
    ["Company", state.companyName || "(unnamed)"],
    ["Total authorised shares", state.authorizedShares],
    ["Total shares outstanding", computed.totalShares],
    ["Total raised", computed.totalRaised],
    ["Post-money valuation", computed.postMoney],
    [],
    ["Holder", "Type", "Shares", "% fully-diluted"],
    ...computed.holders
      .filter(h => h.shares > 0)
      .map(h => [h.name, h.type, h.shares, (h.pct * 100).toFixed(4) + "%"]),
  ];
  return rows.map(r =>
    r.map(cell => {
      const s = String(cell ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")
  ).join("\n");
}

export function loadInitialState() {
  const state = defaultState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) Object.assign(state, JSON.parse(raw));
  } catch (e) { /* ignore */ }
  // Fresh ledger → seed two founders so the tool is immediately useful.
  if (state.founders.length === 0 && state.rounds.length === 0) {
    state.founders.push({ id: uid(), name: "You", role: "CEO", shares: 4_500_000 });
    state.founders.push({ id: uid(), name: "Co-founder", role: "COO", shares: 3_500_000 });
  }
  return state;
}

export function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch (e) { /* private mode or full quota — silently skip */ }
}
